/**
 * `sanitizeReturnTo` — open-redirect guard for the `return_to` parameter.
 *
 * Pure (no `window`, no I/O): the caller passes the current origin. Accepts a
 * root-relative path, or an absolute http(s) URL whose origin is the current
 * origin or explicitly allow-listed. Everything else — protocol-relative
 * `//evil`, `/\evil`, `javascript:`/`data:` URLs, userinfo tricks, backslashes,
 * control characters, and any of those hidden behind percent-encoding — is
 * rejected.
 */

/** Resolves relative input; `.invalid` is reserved and can never be a real origin. */
const SENTINEL_BASE = "https://return-to.invalid";
const MAX_LENGTH = 2048;
const MAX_DECODE_ROUNDS = 3;

// biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point.
const FORBIDDEN_CHARS = /[\u0000-\u001f\u007f\\]/;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** True when `value` smuggles a scheme, an authority or a control/backslash char. */
function isSuspicious(value: string): boolean {
  if (FORBIDDEN_CHARS.test(value)) return true;
  if (value.startsWith("//")) return true;
  // A scheme is only legitimate on a full http(s) URL; those are validated via URL parsing below.
  return HAS_SCHEME.test(value) && !/^https?:\/\//i.test(value);
}

function decodeSafe(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Percent-decode up to {@link MAX_DECODE_ROUNDS} times, checking every form.
 * A malformed escape in the INPUT is treated as an attack; one that only appears
 * after a decode round (a literal `%25` in a query value decodes to a bare `%`)
 * just ends the walk. Input that keeps decoding past the cap is over-encoded: rejected.
 */
function survivesDecoding(value: string): boolean {
  let current = value;
  for (let round = 0; round <= MAX_DECODE_ROUNDS; round++) {
    if (isSuspicious(current)) return false;
    const decoded = decodeSafe(current);
    if (decoded === null) return round > 0;
    if (decoded === current) return true;
    current = decoded;
  }
  return false;
}

function normalizeOrigins(origins: readonly string[]): Set<string> {
  const set = new Set<string>();
  for (const origin of origins) {
    try {
      const parsed = new URL(origin);
      if (parsed.protocol === "https:" || parsed.protocol === "http:") set.add(parsed.origin);
    } catch {
      // ignore malformed allow-list entries
    }
  }
  return set;
}

/**
 * @param href - Candidate return URL (typically `window.location.href`).
 * @param allowedOrigins - Extra origins that may be returned to (e.g. sibling apps).
 * @param currentOrigin - The caller's own origin; always allowed when given.
 * @returns A safe URL string (root-relative path or absolute http(s) URL), or
 *   `null` when `href` must not be used as a redirect target.
 */
export function sanitizeReturnTo(
  href: string,
  allowedOrigins: readonly string[] = [],
  currentOrigin?: string
): string | null {
  if (typeof href !== "string") return null;
  const candidate = href.trim();
  if (candidate === "" || candidate.length > MAX_LENGTH) return null;
  if (!survivesDecoding(candidate)) return null;

  let resolved: URL;
  try {
    resolved = new URL(candidate, SENTINEL_BASE);
  } catch {
    return null;
  }

  // Relative input: must be an explicit root-relative path and must still be one after normalization
  // (`/..//evil.com` normalizes to `//evil.com`).
  if (resolved.origin === SENTINEL_BASE) {
    if (!candidate.startsWith("/") || resolved.pathname.startsWith("//")) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  }

  if (resolved.protocol !== "https:" && resolved.protocol !== "http:") return null;
  if (resolved.username !== "" || resolved.password !== "") return null;

  const allowed = normalizeOrigins(currentOrigin ? [...allowedOrigins, currentOrigin] : allowedOrigins);
  return allowed.has(resolved.origin) ? resolved.href : null;
}
